/**
 * Templates Service
 *
 * GET /templates/all — every library template across every category. Envelope is a flat array:
 *   { result, payload: LibraryTemplate[] }
 * GET /templates/categories/{id} — library templates in one category. Envelope is wrapped:
 *   { result, payload: { templates: LibraryTemplate[] } }
 * The two routes share an item shape but NOT an envelope — confirmed against the real upstream
 * passthrough (`larry-load-api/src/services/templates_service.go`, `larry-common-library/.../upstream_models/templates.go`).
 *
 * GET /templates/custom — the customer's own saved templates.
 *   { result, payload: { customerTemplates: CustomerTemplate[] } }
 *
 * GET /templates/keywords — the full keyword list, for the keyword picker's own options. No
 * endpoint takes a keyword filter param — matching is client-side, mirroring Backgrounds' own
 * `filterBackgroundsByKeywords`, using each library template's own (optional) `cmsKeywordInfo`.
 * A customer template never carries keyword info, so it never matches an active keyword filter.
 *
 * GET /templates/categories — the real category list (id + name). Does NOT include "All" or
 * "Custom Templates" — see `lib/templates/template-category-selection.ts`.
 */

import { httpClient } from '@/api/http/client'
import type {
  AllTemplatesResponse,
  CustomerTemplate,
  CustomerTemplatesResponse,
  LibraryTemplate,
  LibraryTemplatesResponse,
  TemplateCategoriesResponse,
  TemplateCategory,
  TemplateCmsKeyword,
  TemplateKeywordsResponse,
  TemplateLayer,
  TemplateLayerHints,
  TemplateLayerRef,
  TemplateLayerResponse,
} from '@/api/types/template'

async function getAllTemplates(): Promise<LibraryTemplate[]> {
  const response = await httpClient.get<AllTemplatesResponse>('/templates/all')

  if (!response.result || !Array.isArray(response.payload)) {
    throw new Error('Failed to load templates')
  }

  return response.payload
}

async function getTemplatesByCategory(categoryId: number): Promise<LibraryTemplate[]> {
  const response = await httpClient.get<LibraryTemplatesResponse>(
    `/templates/categories/${encodeURIComponent(String(categoryId))}`
  )

  if (!response.result || !Array.isArray(response.payload?.templates)) {
    throw new Error('Failed to load templates for this category')
  }

  return response.payload.templates
}

async function getCustomerTemplates(): Promise<CustomerTemplate[]> {
  const { result, payload } = await httpClient.get<CustomerTemplatesResponse>('/templates/custom')

  if (!result || !Array.isArray(payload?.customerTemplates)) {
    throw new Error('Failed to load your templates')
  }

  return payload.customerTemplates
}

async function getTemplateKeywords(): Promise<TemplateCmsKeyword[]> {
  const { result, payload } = await httpClient.get<TemplateKeywordsResponse>('/templates/keywords')

  if (!result || !Array.isArray(payload)) {
    throw new Error('Failed to load template keywords')
  }

  return payload
}

async function getTemplateCategories(): Promise<TemplateCategory[]> {
  const { result, payload } =
    await httpClient.get<TemplateCategoriesResponse>('/templates/categories')

  if (!result || !Array.isArray(payload?.templateCategoryNames)) {
    throw new Error('Failed to load template categories')
  }

  return payload.templateCategoryNames
}

/**
 * GET /templates/library/{templateId}/layer or GET /templates/custom/{customerTemplateId}/layer —
 * the template's real elements and linked text, ready to add to a spread (see
 * `TemplateLayerResponse`). Nothing is persisted. The ids, fractional indexes and uids in the
 * response depend on `hints`, so a response is good for one placement and must not be cached.
 */
async function getTemplateLayer(
  { source, id }: TemplateLayerRef,
  hints: TemplateLayerHints = {}
): Promise<TemplateLayer> {
  const query = new URLSearchParams()

  if (hints.uidSeed != null) query.set('uidSeed', String(hints.uidSeed))
  if (hints.after) query.set('after', hints.after)

  const queryString = query.size > 0 ? `?${query.toString()}` : ''
  const response = await httpClient.get<TemplateLayerResponse>(
    `/templates/${source}/${encodeURIComponent(id)}/layer${queryString}`
  )

  if (!Array.isArray(response?.stowedElements)) {
    throw new Error('Failed to load this template')
  }

  return {
    elements: response.stowedElements,
    textChains: response.linkedTextElements?.textChain ?? [],
  }
}

export const templatesService = {
  getAllTemplates,
  getTemplatesByCategory,
  getCustomerTemplates,
  getTemplateKeywords,
  getTemplateCategories,
  getTemplateLayer,
}
